import React from 'react';
import { CountryExtendedInfo, formatPopulation, formatArea } from '@/data/country-info';
import { 
  MapPinIcon, 
  UsersIcon, 
  GlobeAltIcon, 
  ClockIcon, 
  BanknotesIcon, 
  ChatBubbleLeftRightIcon, 
  BuildingOffice2Icon,
  MapIcon
} from '@heroicons/react/24/outline';

interface Props {
  countryInfo: CountryExtendedInfo;
  countryName: string;
}

const InfoItem = ({ 
  icon: Icon, 
  label, 
  value, 
}: { 
  icon: React.ElementType, 
  label: string, 
  value: React.ReactNode, 
}) => {
  return (
    <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
      <div className="shrink-0 mt-0.5 text-gray-400">
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-sm font-medium text-gray-500 mb-0.5">{label}</p>
        <div className="font-semibold text-gray-900 text-sm md:text-base leading-snug">{value}</div>
      </div>
    </div>
  );
};

const CountryInfoCard: React.FC<Props> = ({ countryInfo, countryName }) => {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm my-8 overflow-hidden">
      <div className="px-6 py-5 border-b border-gray-100 bg-gray-50/50">
        <h2 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
          {countryName}
          <span className="text-gray-400 font-normal text-lg">Quick Facts</span>
        </h2>
      </div>
      
      <div className="p-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-4">
          
          {countryInfo.capital && (
            <InfoItem 
              icon={MapPinIcon} 
              label="Capital" 
              value={countryInfo.capital} 
            />
          )}
          
          {countryInfo.region && (
            <InfoItem 
              icon={GlobeAltIcon} 
              label="Region" 
              value={
                <span>
                  {countryInfo.region}
                  {countryInfo.subregion && <span className="text-gray-500 font-normal">, {countryInfo.subregion}</span>}
                </span>
              } 
            />
          )}

          {countryInfo.population && (
            <InfoItem 
              icon={UsersIcon} 
              label="Population" 
              value={formatPopulation(countryInfo.population)} 
            />
          )}

          {countryInfo.area && (
            <InfoItem 
              icon={MapIcon} 
              label="Area" 
              value={formatArea(countryInfo.area)} 
            />
          )}

          {countryInfo.currency && (
            <InfoItem 
              icon={BanknotesIcon} 
              label="Currency" 
              value={`${countryInfo.currency.name} (${countryInfo.currency.code})`} 
            />
          )}

          {countryInfo.timezone && (
            <InfoItem 
              icon={ClockIcon} 
              label="Time Zone" 
              value={countryInfo.timezone} 
            />
          )}

          {countryInfo.languages && countryInfo.languages.length > 0 && (
            <InfoItem 
              icon={ChatBubbleLeftRightIcon} 
              label="Languages" 
              value={countryInfo.languages.slice(0, 3).join(", ")}
            />
          )}

          {countryInfo.majorCities && countryInfo.majorCities.length > 0 && (
            <InfoItem 
              icon={BuildingOffice2Icon} 
              label="Major Cities" 
              value={countryInfo.majorCities.slice(0, 2).join(", ")}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default CountryInfoCard;
